import React, { useContext } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { type Client } from '@/types';
import {
  Building2,
  CreditCard,
  Mail,
  MapPin,
  Phone,
} from 'lucide-react';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { formatCrAddress } from '@/lib/crTerritorialDivision';
import { useCrLocations } from '@/hooks/useCrLocations';

interface ClientsDetailModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  client: Client | null;
}

export default function ClientsDetailModal({
  isOpen,
  onOpenChange,
  client,
}: ClientsDetailModalProps) {
  const { translator } = useContext(LanguageProviderContext);
  const { provinces, cantons, districts } = useCrLocations(
    client?.address?.province,
    client?.address?.canton
  );

  const addressLine = formatCrAddress(client?.address, {
    provinces,
    cantons,
    districts,
  });

  const phoneDisplay = client?.phone?.number
    ? [client.phone.country_code, client.phone.number].filter(Boolean).join(' ')
    : '';

  if (!client) {
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{translator('Clients.client')}</DialogTitle>
            <DialogDescription>
              {translator('Global.noData') || 'No client selected.'}
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  const detailRows = [
    {
      icon: CreditCard,
      label: translator('Global.identification'),
      value: client.identification,
    },
    {
      icon: Phone,
      label: translator('Global.phoneNumber'),
      value: phoneDisplay,
    },
    {
      icon: Mail,
      label: translator('Global.email'),
      value: client.email,
    },
    {
      icon: MapPin,
      label: translator('Global.address'),
      value: addressLine,
    },
  ].filter((row) => row.value);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            {client.name}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-4 pt-2">
              {detailRows.map(({ icon: Icon, label, value }) => (
                <div
                  key={label}
                  className="flex items-start gap-3 text-sm"
                >
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-muted-foreground">{label}</p>
                    <p className="mt-0.5 break-words">{value}</p>
                  </div>
                </div>
              ))}
            </div>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}
